// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import ReactPlayer from 'react-player';
import screenfull from 'screenfull';
import Spinner from 'design-system/Spinner';
import { useAsync } from 'react-use-v13';
import clsx from 'clsx';
import { MOBILE_THRESHOLD } from '@scale/frontend-lib/util';
import { Button } from 'design-system';

const GREY_PIXEL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mPM+w8AAeEBb2u5MeEAAAAASUVORK5CYII=';

const Video = ({
  srcVideoUrl,
  onLoadOrError,
  caption = '',
  onEnded,
  onPlay = () => {},
  onPause = null,
  allowFileDownload = false,
  allowPlaybackRate = false,
  forceFullscreen = false,
  onlyVisibleOnLoadComplete = false,
  preventSkip = false,
  maximumExtraPlayingTime,
  disableThumbnail = false,
  playing = false,
  skipSourceVideoLoadingCheck = false,
  onlyPlayWhenFullscreen = false,
  disableVideoScaling = false,
  showCopyCurrentTimeButton = false,
}) => {
  // based on notes from https://github.com/cookpete/react-player/issues/323
  const config = {
    file: {
      attributes: {
        controlsList: `${!allowFileDownload && `nodownload`} ${
          !allowPlaybackRate && `noplaybackrate`
        }`,
      },
    },
  };

  const ref = useRef<ReactPlayer>();

  const [currentTime, setCurrentTime] = useState(0);
  const [playedThrough, setPlayedThrough] = useState(false);
  const [startTime, setStartTime] = useState(0);
  const [forceStop, setForceStop] = useState(false);

  const {
    loading,
    value: videoUrl,
    error,
  } = useAsync(async () => {
    if (!onlyVisibleOnLoadComplete && skipSourceVideoLoadingCheck) {
      return srcVideoUrl;
    }
    const res = await fetch(srcVideoUrl);
    if (!res.ok) {
      // HACK: this is for snowberry, who may arbitrarily remove videos
      await onEnded();

      throw new Error('Failed to load src content');
    }
    if (onlyVisibleOnLoadComplete) {
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } else {
      return srcVideoUrl;
    }
  }, [srcVideoUrl]);

  const [didCopy, setDidCopy] = useState(false);
  const handleCopyTime = () => {
    let time = ref.current?.getCurrentTime();
    if (!time) {
      time = currentTime;
    }
    navigator.clipboard.writeText(time.toString());
    setDidCopy(true);
  };
  useEffect(() => {
    if (didCopy) {
      const timeout = setTimeout(() => setDidCopy(false), 1000);
      return () => clearTimeout(timeout);
    }
  }, [didCopy]);

  return (
    <div className="task-attachment__item task-attachment__item--video">
      {loading && (
        <div style={videoStyles.spinner}>
          <Spinner />
        </div>
      )}
      {!loading && !error && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            visibility: 'visible',
          }}
          className={clsx(disableVideoScaling && 'player-container')}
        >
          <ReactPlayer
            ref={ref}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            width="100%"
            height="100%"
            light={disableThumbnail ? GREY_PIXEL : false}
            playing={!forceStop && (disableThumbnail || playing)}
            onStart={() => {
              setStartTime(Math.round(Date.now() / 1000));
              onLoadOrError(true);
            }}
            onPlay={() => {
              setForceStop(false);

              onPlay && onPlay();
              forceFullscreen &&
                screenfull.isEnabled &&
                !screenfull.isFullscreen &&
                screenfull.request(ref.current.wrapper);
            }}
            onPause={() => {
              onPause && onPause();
              forceFullscreen &&
                screenfull.isEnabled &&
                screenfull.isFullscreen &&
                screenfull.toggle();
            }}
            onError={function () {
              setPlayedThrough(false);
              onLoadOrError(false);
              onEnded();
            }}
            onSeek={() => {
              if (preventSkip && !playedThrough) {
                const delta = ref.current.getCurrentTime() - currentTime;
                if (delta > 0.01) {
                  ref.current.seekTo(currentTime, 'seconds');
                }
              }
            }}
            onProgress={({ playedSeconds }) => {
              if (preventSkip && !playedThrough) {
                const delta = ref.current.getCurrentTime() - currentTime;
                if (delta > 3 || (onlyPlayWhenFullscreen && !screenfull.isFullscreen)) {
                  ref.current.seekTo(currentTime, 'seconds');
                  setForceStop(true);
                } else {
                  setCurrentTime(playedSeconds);
                }
              } else if (onlyPlayWhenFullscreen && !screenfull.isFullscreen) {
                ref.current.seekTo(currentTime, 'seconds');
                setForceStop(true);
              } else {
                setCurrentTime(playedSeconds);
              }
            }}
            onEnded={() => {
              if (disableThumbnail) {
                ref.current.showPreview();
              }
              if (forceFullscreen && screenfull.isEnabled && !onlyPlayWhenFullscreen) {
                screenfull.toggle();
              }
              if (maximumExtraPlayingTime) {
                const actualVideoDuration = currentTime;
                const expectedVideoDuration =
                  Math.round(Date.now() / 1000) - startTime + maximumExtraPlayingTime;
                if (actualVideoDuration > expectedVideoDuration) {
                  alert(
                    'Playback of this video took significantly longer than its duration.\n Please close other tabs and processes, then refresh the task.',
                  );
                  return;
                }
              }
              setPlayedThrough(true);
              onEnded();
            }}
            url={videoUrl}
            controls
            config={config}
          />
        </div>
      )}
      {showCopyCurrentTimeButton && (
        <Button
          type={didCopy ? 'success' : 'primary'}
          style={videoStyles.copyButton}
          onClick={handleCopyTime}
        >
          {didCopy ? 'Copied' : 'Copy current time'}
        </Button>
      )}

      {error && (
        <div style={videoStyles.topbar}>
          <div style={videoStyles.caption}>Failed to load video. Please try refreshing.</div>
        </div>
      )}

      {forceStop && (
        <div style={videoStyles.topbar}>
          <div style={videoStyles.caption}>You have to go fullscreen to view this video.</div>
        </div>
      )}

      {!!caption && (
        <div style={videoStyles.topbar}>
          <div style={videoStyles.caption}>{caption}</div>
        </div>
      )}
      <style jsx>{`
        .player-container :global(video) {
          width: unset !important;
          height: unset !important;
          max-width: 100%;
          max-height: 100%;
        }
        @media (max-width: ${MOBILE_THRESHOLD}) {
          .task-attachment__item--video {
            height: 400px;
          }
        }
      `}</style>
    </div>
  );
};

const videoStyles = {
  topbar: {
    position: 'absolute',
    left: '40px',
    top: '12px',
    display: 'flex',
    flexDirection: 'row',
  },
  caption: {
    background: '#FFFFFF',
    boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.15)',
    borderRadius: '4px',
    textAlign: 'center',
    paddingLeft: '8px',
    paddingRight: '8px',
    paddingTop: '4px',
    paddingBottom: '4px',
    margin: '4px',
  },
  spinner: {
    position: 'absolute',
    height: '100%',
    width: '100%',
  },
  copyButton: {
    position: 'absolute',
    left: '12px',
    top: '12px',
  },
};

export default Video;
